import { Spinner } from '@sjuni/ui';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { isAuthenticated, pendingStep } from '@/lib/auth/session';
import { useAuth } from '@/lib/auth/useAuth';

/**
 * The gate in front of every screen that is not part of signing in.
 *
 * It reads the *status* on the session, not merely the presence of a token:
 * docs/04-API-SPEC.md §5 issues a token at each step of the flow, and only the
 * last one may reach the dashboard. Someone holding a challenge token is sent
 * to finish the challenge rather than to the login form they have already
 * passed.
 *
 * This is a routing convenience. The authorisation is on the server, where a
 * half-way token is refused by its abilities regardless of what the client
 * decides to render.
 */
export function RequireAuth() {
  const { t } = useTranslation();
  const location = useLocation();
  const { session, user, isRestoring } = useAuth();

  const step = pendingStep(session);

  if (step !== null) {
    return <Navigate to={step} replace state={{ from: location.pathname + location.search }} />;
  }

  if (!isAuthenticated(session)) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  /*
   * A stored token but no user yet: `me` is in flight. Rendering the dashboard
   * now would flash empty permission-gated navigation and then fill it in,
   * which reads as the sidebar losing items.
   *
   * The missing *user* is the condition, not the flag on its own. A restore
   * that ends without one — the server unreachable, rather than the token
   * refused — leaves `isRestoring` false with nothing to render permissions
   * from, and the flag alone would let the whole dashboard through as an
   * account that appears to hold no permission at all. Waiting is the honest
   * answer to "we do not know who this is yet"; a reload retries.
   */
  if (isRestoring || user === null) {
    return (
      <div className="grid min-h-screen place-items-center">
        <Spinner size="lg" label={t('common.loading')} />
      </div>
    );
  }

  return <Outlet />;
}
